import { useState, useEffect } from 'react';
import '@/App.css';
import MainMenu from '@/screens/MainMenu';
import Customize from '@/screens/Customize';
import Results from '@/screens/Results';
import GameCanvas from '@/components/GameCanvas';
import PaymentReturn from '@/screens/PaymentReturn';
import { audio } from '@/game/audio';

const SKIN_COLORS = ['#ff5a5f', '#4d8bff', '#3ddc84', '#ffd23f', '#b06bff', '#ff8a3d', '#ff6fb5', '#2ee6d6'];
const DEFAULT_OWNED = { hats: ['none', 'cap', 'beanie'], effects: ['none'], trails: ['none'], auras: ['none'], gadgets: ['none'], pets: ['none'], skins: ['none'] };

const loadProfile = () => {
  let p = {};
  try { p = JSON.parse(localStorage.getItem('so_profile')) || {}; } catch {}
  const owned = p.owned || {};
  const oc = (k) => (owned[k] && owned[k].length ? owned[k] : DEFAULT_OWNED[k]);
  return {
    name: p.name || 'Snacker' + Math.floor(Math.random() * 1000),
    color: p.color || SKIN_COLORS[Math.floor(Math.random() * SKIN_COLORS.length)],
    hat: p.hat || 'cap',
    effect: p.effect || 'none',
    trail: p.trail || 'none',
    aura: p.aura || 'none',
    gadget: p.gadget || 'none',
    pet: p.pet || 'none',
    skin: p.skin || 'none',
    coins: typeof p.coins === 'number' ? p.coins : 0,
    keys: typeof p.keys === 'number' ? p.keys : 0,
    owned: {
      hats: oc('hats'), effects: oc('effects'), trails: oc('trails'),
      auras: oc('auras'), gadgets: oc('gadgets'), pets: oc('pets'), skins: oc('skins'),
    },
  };
};
const loadSettings = () => {
  try { const s = JSON.parse(localStorage.getItem('so_settings')); if (s) return s; } catch {}
  return { sound: true, music: true };
};

function App() {
  const path = window.location.pathname;
  const initialScreen = path.includes('/payment/success') ? 'pay-success'
    : path.includes('/payment/cancel') ? 'pay-cancel' : 'menu';
  const [screen, setScreen] = useState(initialScreen);
  const [profile, setProfile] = useState(loadProfile);
  const [settings, setSettings] = useState(loadSettings);
  const [results, setResults] = useState(null);

  useEffect(() => { localStorage.setItem('so_profile', JSON.stringify(profile)); }, [profile]);
  useEffect(() => { localStorage.setItem('so_settings', JSON.stringify(settings)); }, [settings]);
  useEffect(() => { audio.setSfx(settings.sound); audio.musicOn = settings.music; }, [settings.sound, settings.music]);

  const startGame = () => { setResults(null); setScreen('game'); };
  const handleExit = (r) => { if (r) setResults(r); setScreen(r ? 'results' : 'menu'); };
  const addCoins = (n) => setProfile((p) => ({ ...p, coins: (p.coins || 0) + n }));
  const addKeys = (n) => setProfile((p) => ({ ...p, keys: (p.keys || 0) + n }));
  const updateProfile = (partial) => setProfile((p) => ({ ...p, ...partial }));
  const applyGrant = (g) => setProfile((p) => {
    const owned = { ...(p.owned || {}) };
    for (const it of (g.items || [])) {
      const list = owned[it.cat] ? [...owned[it.cat]] : [...(DEFAULT_OWNED[it.cat] || [])];
      if (!list.includes(it.id)) list.push(it.id);
      owned[it.cat] = list;
    }
    return { ...p, coins: (p.coins || 0) + (g.coins || 0), keys: (p.keys || 0) + (g.keys || 0), owned };
  });

  // Reconcile any pending purchases (interrupted returns) once on load — server-authoritative, exactly-once.
  useEffect(() => {
    let pend = [];
    try { pend = JSON.parse(localStorage.getItem('so_pending') || '[]'); } catch {}
    if (!pend.length) return;
    const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    (async () => {
      const remaining = [];
      for (const sid of pend) {
        try {
          const r = await fetch(API + '/payments/claim', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sid }),
          });
          const d = await r.json();
          if (d.granted) applyGrant({ coins: d.coins || 0, keys: d.keys || 0, items: d.items || [] });
          else if (!d.already_credited && d.paid === false && !['expired', 'failed'].includes(d.payment_status)) remaining.push(sid);
        } catch { remaining.push(sid); }
      }
      localStorage.setItem('so_pending', JSON.stringify(remaining));
    })();
    // eslint-disable-next-line
  }, []);


  if (screen === 'pay-success' || screen === 'pay-cancel') {
    return (
      <div className="App so-app">
        <PaymentReturn mode={screen === 'pay-cancel' ? 'cancel' : 'success'}
          onGrant={applyGrant} onDone={() => setScreen('menu')} />
      </div>
    );
  }

  return (
    <div className="App so-app">
      {screen === 'menu' && (
        <MainMenu profile={profile} settings={settings} onPlay={startGame}
          onCustomize={() => setScreen('customize')} onSettings={(s) => setSettings(s)}
          onChange={updateProfile} />
      )}
      {screen === 'customize' && (
        <Customize profile={profile} onChange={updateProfile} onBack={() => setScreen('menu')} />
      )}
      {screen === 'game' && (
        <GameCanvas profile={profile} settings={settings} onExit={handleExit} onCoins={addCoins} onKeys={addKeys} />
      )}
      {screen === 'results' && (
        <Results results={results} profile={profile} onRematch={startGame} onMenu={() => setScreen('menu')} />
      )}
    </div>
  );
}

export default App;
